import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ObjectID from "bson-objectid";
import {
  getEmotionSettings,
  createEmotionSettings,
  deleteEmotionSetting,
  updateEmotionSetting,
} from "../api/emotion-settings.api";
import { useBootstrap } from "../features/bootstrap";

/**
 * Normalise any userId shape into plain string, or null if invalid
 */
const normalizeId = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return ObjectID.isValid(value) ? value : null;
  }

  if (typeof value === "object") {
    return value?._id.toString?.() ?? value?.id.toString() ?? null;
  }

  return null;
};

/**
 * Build the query-key filters object - strips undefined values so
 * React Query treats { scope: undefined } and {} as the same key.
 */
const buildQueryKeyFilters = (filters = {}) => {
  const params = {};

  if (filters.active !== undefined) {
    params.active = filters.active;
  }

  if (filters.scope) {
    params.scope = filters.scope;
  }

  if (filters.entityId !== undefined) {
    if (filters.entityId === null) {
      params.entityId = null;
    } else {
      const entities = Array.isArray(filters.entityId)
        ? filters.entityId
        : [filters.entityId];

      params.entityId = entities.map(normalizeId).filter(Boolean);
    }
  }

  return params;
};

/**
 * Empty grouped structure used as the cache default.
 */
const emptyGrouped = () => ({
  byScope: { default: [], district: [], school: [], custom: [] },
  byCreator: {},
});

export const useEmotionSettings = () => {
  const { data: user } = useBootstrap();
  const queryClient = useQueryClient();
  const normalizedId = normalizeId(user._id);

  const queryKey = ["emotion-settings", normalizedId];
  const isEnabled = !!normalizedId;

  const emotionSettingsQuery = useQuery({
    queryKey,
    queryFn: getEmotionSettings,
    enabled: isEnabled,
    staleTime: 30_000,
  });

  const createEmotionSettingsMutation = useMutation({
    mutationFn: (settingsData) => createEmotionSettings(settingsData),
    onMutate: async (settingsData) => {
      if (!Array.isArray(settingsData) || settingsData.length === 0) {
        throw new Error("Settings data must be a non-empty array");
      }

      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData(queryKey);

      const optimisticItems = settingsData.map((s) => {
        const { entityId } = buildQueryKeyFilters(s);

        return {
          ...s,
          _id: `optimistic-${crypto.randomUUID()}`,
          emotion: s.emotion?.toLowerCase().trim(),
          entityId,
          active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      });

      queryClient.setQueryData(queryKey, (old) => {
        const base = old ?? emptyGrouped();

        const clone = {
          byScope: structuredClone(base.byScope ?? {}),
          byCreator: structuredClone(base.byCreator ?? {}),
        };

        optimisticItems.forEach((item) => {
          const scope = item.scope;

          if (!clone.byScope[scope]) {
            clone.byScope[scope] = [];
          }

          clone.byScope[item.scope].unshift(item);

          const creatorId = item.createdBy?.toString?.();

          if (!clone.byCreator[creatorId]) {
            clone.byCreator[creatorId] = [];
          }

          clone.byCreator[creatorId].unshift(item);
        });
        return clone;
      });

      return { snapshot, optimisticItems };
    },
    onSuccess: (serverItems, _variables, context) => {
      if (!context?.optimisticItems?.length) {
        return;
      }

      queryClient.setQueryData(queryKey, (old) => {
        const base = old ?? emptyGrouped();

        const clone = {
          byScope: structuredClone(base.byScope),
          byCreator: structuredClone(base.byCreator),
        };

        const optimisticIds = new Set(
          context.optimisticItems.map((item) => item._id.toString?.()),
        );

        Object.keys(clone.byScope).forEach((scope) => {
          clone.byScope[scope] = clone.byScope[scope].filter(
            (item) => !optimisticIds.has(item._id.toString()),
          );
        });

        serverItems.forEach((item) => {
          const scope = item.scope;

          if (!clone.byScope[scope]) {
            clone.byScope[scope] = [];
          }

          clone.byScope[scope].unshift(item);

          const creatorId = item.createdBy?.toString?.();

          if (!creatorId) {
            return;
          }

          if (!clone.byCreator[creatorId]) {
            clone.byCreator[creatorId] = [];
          }

          clone.byCreator[creatorId].unshift(item);
        });

        return clone;
      });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(queryKey, context.snapshot);
      }
    },
  });

  const updateEmotionSettingMutation = useMutation({
    mutationFn: ({ id, updates }) => updateEmotionSetting({ id, updates }),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const snapshot = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        const base = old ?? emptyGrouped();

        const clone = {
          byScope: structuredClone(base.byScope),
          byCreator: structuredClone(base.byCreator),
        };

        const applyUpdate = (item) =>
          item._id.toString() === id.toString()
            ? { ...item, ...updates, updatedAt: new Date().toISOString() }
            : item;

        Object.keys(clone.byScope).forEach((scope) => {
          clone.byScope[scope] = clone.byScope[scope].map(applyUpdate);
        });

        Object.keys(clone.byCreator).forEach((creator) => {
          clone.byCreator[creator] = clone.byCreator[creator].map(applyUpdate);
        });

        return clone;
      });

      return { snapshot };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(queryKey, context.snapshot);
      }
    },
  });

  const deleteEmotionSettingMutation = useMutation({
    mutationFn: (id) => deleteEmotionSetting(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });

      const snapshot = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        const base = old ?? emptyGrouped();

        const clone = {
          byScope: structuredClone(base.byScope ?? {}),
          byCreator: structuredClone(base.byCreator ?? {}),
        };

        Object.keys(clone.byScope).forEach((scope) => {
          clone.byScope[scope] = clone.byScope[scope].filter(
            (item) => item._id !== id,
          );
        });

        Object.keys(clone.byCreator).forEach((creator) => {
          clone.byCreator[creator] = clone.byCreator[creator].filter(
            (item) => item._id !== id,
          );
        });

        return clone;
      });

      return { snapshot };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(queryKey, context.snapshot);
      }
    },
  });

  return {
    emotionSettingsQuery,
    createEmotionSettingsMutation,
    updateEmotionSettingMutation,
    deleteEmotionSettingMutation,
  };
};
